import React, { useEffect, useMemo, useRef } from "react";
import { ButtonControl } from "spark-core-dx/components";
import {
  getDisplayName,
  getValue
} from "../resourceConfigWizardUtils";
import { StepField } from "./WizardStepCommon";

const fallbackFields = [
  {
    FieldName: "DisplayName",
    DisplayName: "Configuration Name",
    Description: "Name shown for this configured source bundle.",
    IsRequired: true,
    FieldTypeKey: "TextBox"
  },
  {
    FieldName: "Notes",
    DisplayName: "Notes",
    Description: "Optional setup notes for the resource owner.",
    IsRequired: false,
    FieldTypeKey: "TextArea"
  }
];

const getFieldName = (field) => getValue(field, "FieldName", "fieldName") || "";

const getResourceDetailDefaults = (context) => {
  const configuration = getValue(context.wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  return {
    DisplayName: getDisplayName(configuration) || getValue(context.wizard, "ResourceName", "resourceName") || "",
    Notes: getValue(configuration, "Notes", "notes") || ""
  };
};

const buildResourceDetailValues = (context, fields) => {
  const defaults = getResourceDetailDefaults(context);
  return fields.reduce((result, field) => {
    const fieldName = getFieldName(field);
    result[fieldName] = context.values?.[fieldName] ?? defaults[fieldName] ?? "";
    return result;
  }, {});
};

const ResourceDetailsStep = ({ context, onChange, onSave, onContinue, registerStepController }) => {
  const { readOnly, saving, values, wizard } = context;
  const resolvedValuesRef = useRef({});
  const fields = useMemo(
    () => (context.fields?.length ? context.fields : fallbackFields),
    [context.fields]
  );
  const resolvedValues = useMemo(
    () => buildResourceDetailValues({ wizard, values }, fields),
    [fields, values, wizard]
  );
  resolvedValuesRef.current = resolvedValues;

  useEffect(() => {
    registerStepController?.({
      prepareSave: async (currentValues = {}) => ({
        ...resolvedValuesRef.current,
        ...currentValues
      }),
      prepareContinue: async (currentValues = {}) => ({
        ...resolvedValuesRef.current,
        ...currentValues
      })
    });
    return () => registerStepController?.(null);
  }, [registerStepController]);

  return (
    <>
      <div className="resource-wizard__fields">
        {fields.map((field) => {
          const fieldName = getFieldName(field);
          const value = resolvedValues[fieldName] || "";
          return (
            <StepField
              key={fieldName}
              field={field}
              value={value}
              disabled={readOnly}
              onChange={onChange}
            />
          );
        })}
      </div>
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={() => onSave?.()}
            disabled={readOnly || saving}
            loading={saving}
          >
            Save
          </ButtonControl>
          <ButtonControl
            type="ok-small"
            className="resource-wizard__button resource-wizard__button--primary"
            onClick={() => onContinue?.()}
            disabled={readOnly || saving}
            loading={saving}
          >
            Save & Continue
          </ButtonControl>
        </div>
      </div>
    </>
  );
};

export default ResourceDetailsStep;
